import { QueryClient } from "@tanstack/react-query"
import { createTRPCClient, httpBatchStreamLink, httpLink, splitLink } from "@trpc/client"
import { httpBatchLink } from "@trpc/client/links/httpBatchLink"
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server"
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query"
import type { AppRouter } from "../../../../services/api/src/trpc/trpc-types"
import { getCookie } from "./cookies/get-cookie"

let apiUrl = "https://api-internal.tokenterminal.com"

if (process.env.NEXT_PUBLIC_USE_LOCAL_SERVER === `true`) {
  apiUrl = "http://localhost:3001"
}

if (process.env.NEXT_PUBLIC_VERCEL_ENV === `preview`) {
  apiUrl = "https://api-internal.tokenterminal.dev"
}

const getToken = () => {
  if (getCookie("_idtoken")) {
    return null
  }

  const apiToken =
    process.env.NEXT_PUBLIC_USE_LOCAL_SERVER === `true`
      ? process.env.NEXT_PUBLIC_LOCAL_API_DEFAULT_TOKEN
      : process.env.NEXT_PUBLIC_API_DEFAULT_TOKEN

  return apiToken ?? null
}

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    },
  })
}

/**
 * Browser-side singleton. Server renders must NOT use this shared instance:
 * TanStack Query's HydrationBoundary only hydrates queries that don't
 * already exist in the cache synchronously during render — entries left in
 * this singleton by earlier requests (often stuck `pending`, since fetches
 * never start during SSR) would defer hydration to a useEffect that never
 * runs on the server, silently un-SSRing that data. The provider creates a
 * fresh client per server render instead.
 */
export const queryClient = createQueryClient()

const RETRYABLE_HTTP_CODES = new Set([429, 502, 503, 504])
const MAX_RETRIES = 3

/** Wraps a fetch function with server-side retry for transient HTTP errors (429, 502–504). */
function withServerRetry(baseFetch: typeof fetch): typeof fetch {
  return async (url, init) => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const res = await baseFetch(url, init)
      if (!RETRYABLE_HTTP_CODES.has(res.status) || attempt === MAX_RETRIES) return res
      const delay = Math.min(500 * 2 ** attempt, 4000)
      console.warn(
        `[trpc] Retrying ${typeof url === "string" ? url : url.toString()} (status ${res.status}, attempt ${attempt + 1}/${MAX_RETRIES}, delay ${delay}ms)`,
      )
      await new Promise((r) => setTimeout(r, delay))
    }
    // unreachable, but TS needs it
    return baseFetch(url, init)
  }
}

const retryFetch = withServerRetry(fetch)

/** fetch with retry on the server, plain fetch in the browser. */
const serverFetch: typeof fetch = (url, init) =>
  typeof window === "undefined"
    ? retryFetch(url as Parameters<typeof fetch>[0], init)
    : fetch(url as Parameters<typeof fetch>[0], init)

const fetcher = (url: URL | RequestInfo, init?: RequestInit | undefined): Promise<Response> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "x-tt-terminal-jwt": process.env.NEXT_PUBLIC_FRONTEND_JWT ?? "",
    ...(typeof window === "undefined" &&
      process.env.TT_BUILD_SECRET && {
        "x-build-secret": process.env.TT_BUILD_SECRET,
      }),
  }

  if (typeof window !== "undefined") {
    headers["x-app-path"] = window.location.pathname
  }

  if (getToken()) {
    headers.Authorization = `Bearer ${getToken()}`
  }

  return serverFetch(url, {
    ...(init || {}),
    credentials: "include",
    headers,
  })
}

const fetcherStream = (
  url: URL | RequestInfo,
  init?: RequestInit | undefined,
): Promise<Response> => {
  const headers: Record<string, string> = {
    // Don't set Content-Type - let httpBatchStreamLink set it
    "x-tt-terminal-jwt": process.env.NEXT_PUBLIC_FRONTEND_JWT ?? "",
    ...(typeof window === "undefined" &&
      process.env.TT_BUILD_SECRET && {
        "x-build-secret": process.env.TT_BUILD_SECRET,
      }),
  }

  if (typeof window !== "undefined") {
    headers["x-app-path"] = window.location.pathname
  }

  if (getToken()) {
    headers.Authorization = `Bearer ${getToken()}`
  }

  return serverFetch(url, {
    ...(init || {}),
    credentials: "include",
    headers: {
      ...(init?.headers || {}), // Put init headers first (preserves streaming headers)
      ...headers, // Then add custom headers
    },
  })
}

// Per-op threshold for the URL-encoded input above which a query is sent as
// POST instead of GET. Up to maxItems (4) ops share one batched URL, and the
// API proxy caps the request line at 8KB, so keep this conservative.
const LARGE_INPUT_URL_CHARS = 2000

// Queries with large inputs (e.g. asset.getAssets with hundreds of asset ids)
// fail as GET, and the failed request surfaces in the browser as a CORS error.
// Such queries go via POST; small queries stay GET (cacheable, idempotent).
const hasLargeInput = (op: { input: unknown }) =>
  encodeURIComponent(JSON.stringify(op.input ?? {})).length > LARGE_INPUT_URL_CHARS

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition(op) {
        return op.context.skipBatch === true
      },
      true: splitLink({
        condition: hasLargeInput,
        true: httpLink({
          url: `${apiUrl}/trpc`,
          fetch: fetcher,
          methodOverride: "POST",
        }),
        false: httpLink({
          url: `${apiUrl}/trpc`,
          fetch: fetcher,
        }),
      }),
      false: splitLink({
        condition(op) {
          return op.path === "customQuery.generateQueryStream"
        },
        true: httpBatchStreamLink({
          url: `${apiUrl}/trpc`,
          fetch: fetcherStream,
          maxItems: 4,
        }),
        false: splitLink({
          condition: hasLargeInput,
          true: httpBatchLink({
            url: `${apiUrl}/trpc`,
            fetch: fetcher,
            maxItems: 4,
            methodOverride: "POST",
          }),
          false: httpBatchLink({
            url: `${apiUrl}/trpc`,
            fetch: fetcher,
            maxItems: 4,
          }),
        }),
      }),
    }),
  ],
})

/**
 * Minimal request shape for server-side tRPC (e.g. Next.js getServerSideProps context.req).
 * Forward cookie and authorization so the API sees the requesting user.
 */
export type ServerTrpcRequest = {
  headers: { cookie?: string; authorization?: string }
}

/**
 * Create a tRPC client for server-side use (getServerSideProps) that forwards the
 * incoming request's cookie and authorization. Use this so the API auth context
 * matches the requesting user instead of the default token.
 *
 * @param req - Incoming request (e.g. context.req). Must have headers.cookie and optionally headers.authorization.
 * @param options - Optional { authorization: "Bearer <token>" } when session is parsed from cookies (e.g. parseTTAuthCookies).
 */
export function getServerTrpcClient(
  req: ServerTrpcRequest,
  options?: { authorization?: string },
): ReturnType<typeof createTRPCClient<AppRouter>> {
  const auth = options?.authorization ?? req.headers?.authorization
  const serverFetcher = (url: URL | RequestInfo, init?: RequestInit): Promise<Response> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "x-tt-terminal-jwt": process.env.NEXT_PUBLIC_FRONTEND_JWT ?? "",
      ...(process.env.TT_BUILD_SECRET && {
        "x-build-secret": process.env.TT_BUILD_SECRET,
      }),
      ...(req.headers?.cookie && { Cookie: req.headers.cookie }),
      ...(auth && { Authorization: auth }),
    }
    return serverFetch(url, {
      ...init,
      headers: { ...(init?.headers as Record<string, string>), ...headers },
    })
  }
  return createTRPCClient<AppRouter>({
    links: [
      httpLink({
        url: `${apiUrl}/trpc`,
        fetch: serverFetcher,
      }),
    ],
  })
}
export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
})

// infer the types for your router
export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

type MetricConfigurations = RouterOutputs["metrics"]["getMetricsConfiguration"]["data"]
export type MetricConfiguration = MetricConfigurations[number]

type Projects = RouterOutputs["projects"]["getProjects"]["data"]
export type ProjectMeta = Projects[number]

type Chains = RouterOutputs["chains"]["getChains"]["data"]
export type ChainMetadata = Chains[number]

export type ProjectDetails = RouterOutputs["projects"]["getProject"]

type MarketSectors = RouterOutputs["marketSectors"]["getMarketSectors"]["data"]
export type MarketSector = MarketSectors[number]
export type MarketSectorDetails = RouterOutputs["marketSectors"]["getMarketSector"]["data"]

export type ProjectDetailsMeta = RouterOutputs["projects"]["getProject"]

export type ProductMeta = RouterOutputs["products"]["getProducts"]["data"][number]

export type MetricsBreakdownItem = RouterOutputs["metrics"]["postBreakdown"]["data"][number]

export type Assets = RouterOutputs["asset"]["getAssets"]["data"]
export type Asset = Assets[number]
